import { db, storage } from "firebase.js";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  where,
  addDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

const COLLECTION = "mapPoints";

const imageRef = (pointId) => ref(storage, `mapPoints/${pointId}/image`);

// 🔹 Fetch every point displayed on the interactive map (public read)
export const getAllMapPoints = async () => {
  const snapshot = await getDocs(collection(db, COLLECTION));
  console.log(`Firestore read count (mapPoints): ${snapshot.docs.length}`);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const fetchMapPointById = async (pointId) => {
  const snapshot = await getDoc(doc(db, COLLECTION, pointId));
  if (!snapshot.exists()) return null;
  return { id: snapshot.id, ...snapshot.data() };
};

// 🔹 The map point linked to an FQST location (public read), used to show its address.
// Prefers the "weekly" point that represents the location; null when none has an address.
export const fetchMapPointByLocation = async (locationId) => {
  const snapshot = await getDocs(
    query(collection(db, COLLECTION), where("locationId", "==", locationId))
  );
  console.log(`Firestore read count (mapPoints by location): ${snapshot.docs.length}`);
  const points = snapshot.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((p) => p.fullAddress?.trim() || p.address?.trim());
  return points.find((p) => p.type === "weekly") || points[0] || null;
};

// 🔹 Create — returns the new document id
export const createMapPoint = async (data) => {
  const docRef = await addDoc(collection(db, COLLECTION), {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
};

// 🔹 Update
export const updateMapPoint = async (pointId, data) => {
  await updateDoc(doc(db, COLLECTION, pointId), {
    ...data,
    updatedAt: serverTimestamp(),
  });
};

// 🔹 Delete the document and its image (image deletion is best effort)
export const deleteMapPoint = async (pointId) => {
  await removeStoredImage(pointId);
  await deleteDoc(doc(db, COLLECTION, pointId));
};

export const uploadMapPointImage = async (pointId, file) => {
  const storageRef = imageRef(pointId);
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);
  await updateDoc(doc(db, COLLECTION, pointId), {
    image: url,
    updatedAt: serverTimestamp(),
  });
  return url;
};

export const deleteMapPointImage = async (pointId) => {
  await removeStoredImage(pointId);
  await updateDoc(doc(db, COLLECTION, pointId), {
    image: deleteField(),
    updatedAt: serverTimestamp(),
  });
};

const removeStoredImage = async (pointId) => {
  try {
    await deleteObject(imageRef(pointId));
  } catch (err) {
    if (err?.code !== "storage/object-not-found") {
      console.warn("Could not delete map point image:", err);
    }
  }
};

// 🔹 Address → coordinates, via OpenStreetMap's Nominatim (free, no API key).
// Usage policy: low volume only (admin form), which is our case.
// Each result carries the structured parts so the form can save a complete address.
export const geocodeAddress = async (text) => {
  const params = new URLSearchParams({
    q: text,
    format: "jsonv2",
    limit: "5",
    addressdetails: "1",
    countrycodes: "ca",
    "accept-language": "fr",
  });
  const res = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, {
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(`Geocoding failed with status ${res.status}`);
  const results = await res.json();
  return results.map((r) => {
    const a = r.address || {};
    return {
      label: r.display_name,
      lat: parseFloat(r.lat),
      lng: parseFloat(r.lon),
      houseNumber: a.house_number || "",
      road: a.road || a.pedestrian || a.footway || a.street || "",
      town: a.city || a.town || a.village || a.municipality || a.hamlet || "",
      province: a.state || "",
      postalCode: a.postcode || "",
    };
  });
};
